import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Empty, Skeleton, Typography, Space, Tag, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import auctionService from '../../services/auctionService';
import { message } from 'antd';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import AuctionCard from '../../components/Auction/AuctionCard';
import AuctionCardSkeleton from '../../components/Auction/AuctionCardSkeleton';
import CategorySection from '../../components/Auction/CategorySection';
import AuctionCountdown from '../../components/Common/AuctionCountdown';
import FeaturedActiveAuctions from '../../components/Auction/FeaturedActiveAuctions';
import moment from 'moment';
import './CategoryResults.css';
import { 
    TagOutlined, 
    ArrowLeftOutlined, 
    ClockCircleOutlined,
    AppstoreOutlined,
    FireOutlined,
    StarOutlined,
    CloseOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const CategoryResults = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all'
    });
    const [localFilters, setLocalFilters] = useState({
        status: 'all',
        category: 'all'
    });
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const resultsSectionRef = useRef(null);

    // Read URL parameters and load results when component mounts or URL changes
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryQuery = searchParams.get('category');
        const statusQuery = searchParams.get('status');

        const urlFilters = {
            status: statusQuery || 'all',
            category: categoryQuery || 'all'
        };

        // Update component state with URL parameters
        setFilters(urlFilters);
        setLocalFilters(urlFilters);

        // Load category results with URL parameters
        loadCategoryResults(urlFilters);
    }, [location.search]);

    // Separate effect for loading categories
    useEffect(() => {
        if (categories.length === 0) {
            loadCategories();
        } else {
            // If we already have categories, find the selected category
            if (filters.category !== 'all') {
                const category = categories.find(cat => 
                    cat.name.toLowerCase() === filters.category.toLowerCase()
                );
                setSelectedCategory(category);
            } else {
                // Clear selected category when filter is set to 'all'
                setSelectedCategory(null);
            }
        }
    }, [categories, filters.category]);

    // Scroll to results when category changes and results are loaded
    useEffect(() => {
        if (resultsSectionRef.current && filters.category !== 'all' && !loading) {
            // Small delay to ensure DOM is updated after loading completes
            const timer = setTimeout(() => {
                const element = resultsSectionRef.current;
                if (element) {
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for header
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [filters.category, loading, auctions.length]);

    const scrollToResults = () => {
        if (resultsSectionRef.current) {
            const element = resultsSectionRef.current;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for header
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const loadCategories = async () => {
        try {
            setCategoriesLoading(true);
            const categoriesResponse = await auctionService.getCategories();
            const fetchedCategories = categoriesResponse.data;
            setCategories(fetchedCategories);
            
            // Find the selected category from the fetched data
            if (filters.category !== 'all') {
                const category = fetchedCategories.find(cat => 
                    cat.name.toLowerCase() === filters.category.toLowerCase()
                );
                setSelectedCategory(category);
            } else {
                // Clear selected category when filter is set to 'all'
                setSelectedCategory(null);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const loadCategoryResults = async (filtersToUse = null) => {
        try {
            setLoading(true);
            
            const currentFilters = filtersToUse || filters;
            
            const auctionsResponse = await auctionService.getAllAuctions({
                ...currentFilters,
                status: currentFilters.status === 'all' ? undefined : currentFilters.status.toLowerCase(),
                category: currentFilters.category === 'all' ? undefined : currentFilters.category.toLowerCase()
            });

            // Process auctions with favorites
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = auctionsResponse.data.map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));

            setAuctions(auctionsWithFavorites);
        } catch (error) {
            console.error('Error loading category results:', error);
            message.error('Failed to load category results');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        
        const searchParams = new URLSearchParams();
        if (newFilters.category !== 'all') searchParams.set('category', newFilters.category);
        if (newFilters.status !== 'all') searchParams.set('status', newFilters.status);
        
        navigate({
            pathname: '/category-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Load category results with the new filters
        loadCategoryResults(newFilters);
    };

    const handleCategoryChange = (category) => {
        const newFilters = { ...filters, category };
        handleFilterChange(newFilters);
        // Scroll will be handled automatically by useEffect when loading completes
    };

    const handleQuickFilter = (status) => {
        const newLocalFilters = { ...localFilters, status };
        setLocalFilters(newLocalFilters);
        // Don't apply immediately - wait for apply button click
    };

    const applyFilters = () => {
        setFilters(localFilters);
        
        const searchParams = new URLSearchParams();
        if (localFilters.category !== 'all') searchParams.set('category', localFilters.category);
        if (localFilters.status !== 'all') searchParams.set('status', localFilters.status);
        
        navigate({
            pathname: '/category-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Load category results with the new filters
        loadCategoryResults(localFilters);
        
        // Scroll to results after applying filters
        setTimeout(() => {
            scrollToResults();
        }, 400); // Delay to allow data loading to start
    };

    const handleFavoriteToggle = (auctionId) => {
        // Optimistically update local state
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));
    };

    const goBackToAuctions = () => {
        navigate('/auctions');
    };

    const removeFilter = (filterType) => {
        const newFilters = { ...filters };
        
        if (filterType === 'status') {
            newFilters.status = 'all';
        } else if (filterType === 'category') {
            newFilters.category = 'all';
        }
        
        setFilters(newFilters);
        setLocalFilters(newFilters);
        
        // Update URL
        const searchParams = new URLSearchParams();
        if (newFilters.status !== 'all') searchParams.set('status', newFilters.status);
        if (newFilters.category !== 'all') searchParams.set('category', newFilters.category);
        
        navigate({
            pathname: '/category-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Reload category results
        loadCategoryResults(newFilters);
    };

    return (
        <div className="category-results">

            {/* Category Section */}
            <CategorySection
                categories={categories}
                selectedCategory={filters.category}
                onCategoryChange={handleCategoryChange}
                loading={categoriesLoading}
                showAllCategories={true}
                showViewAllButton={false}
            />

            {/* Featured Auctions Section */}
            <div style={{ marginTop: '32px', marginBottom: '32px' }}>
                <FeaturedActiveAuctions
                    limit={6}
                    showViewAll={true}
                    category={selectedCategory ? selectedCategory.name : null}
                    hideIfEmpty={true}
                />
            </div>

            {/* Filter Section */}
            <div className="filter-section" style={{ 
                marginBottom: 32,
                padding: '24px',
                background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
                borderRadius: '16px',
                border: '1px solid #d9f7be',
                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.1)'
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        flexWrap: 'wrap' 
                    }}>
                        <Text style={{ 
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#374151',
                            margin: 0,
                            whiteSpace: 'nowrap'
                        }}>
                            Filter
                        </Text>
                        <Space size="small" wrap>
                            <Button 
                                type={localFilters.status === 'all' ? 'primary' : 'default'}
                                size="small"
                                icon={<AppstoreOutlined />}
                                onClick={() => handleQuickFilter('all')}
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'all' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'all' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                All Auctions
                            </Button>
                            <Button 
                                type={localFilters.status === 'active' ? 'primary' : 'default'}
                                size="small"
                                icon={<FireOutlined />}
                                onClick={() => handleQuickFilter('active')}
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'active' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'active' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Active
                            </Button>
                            <Button 
                                type={localFilters.status === 'upcoming' ? 'primary' : 'default'}
                                size="small"
                                icon={<ClockCircleOutlined />}
                                onClick={() => handleQuickFilter('upcoming')}
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'upcoming' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'upcoming' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Upcoming
                            </Button>
                            <Button 
                                type={localFilters.status === 'completed' ? 'primary' : 'default'}
                                size="small"
                                icon={<StarOutlined />}
                                onClick={() => handleQuickFilter('completed')}
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'completed' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'completed' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Completed
                            </Button>
                        </Space>
                    </div>
                    
                    <Button
                        type="primary"
                        onClick={applyFilters}
                        style={{
                            borderRadius: '20px',
                            fontWeight: 600,
                            height: '32px',
                            padding: '0 20px',
                            fontSize: '13px',
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                        }}
                    >
                        Apply Filters
                    </Button>
                </div>
            </div>

            {/* Enhanced Category Results Section */}
            <div className="category-results-section" ref={resultsSectionRef}>
                {/* Enhanced Header with Animation */}
                <div className="category-header" style={{ 
                    marginBottom: 32,
                    padding: '32px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '20px',
                    boxShadow: '0 8px 32px rgba(82, 196, 26, 0.3)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background Decoration */}
                    <div style={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        zIndex: 1
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: -30,
                        left: -30,
                        width: 150,
                        height: 150,
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: '50%',
                        zIndex: 1
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                            <div style={{
                                padding: '12px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '12px',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <TagOutlined style={{ 
                                    fontSize: '24px', 
                                    color: 'white' 
                                }} />
                            </div>
                            <div>
                                <Title level={2} style={{ 
                                    margin: 0, 
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: '28px',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    {selectedCategory ? `${selectedCategory.name} Auctions` : 'Category Results'}
                                </Title>
                                <Text style={{ 
                                    fontSize: '16px',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 500,
                                    marginTop: '4px'
                                }}>
                                    {selectedCategory 
                                        ? `Found ${auctions.length} auction${auctions.length !== 1 ? 's' : ''} in ${selectedCategory.name} category`
                                        : `Found ${auctions.length} auction${auctions.length !== 1 ? 's' : ''}`
                                    }
                                </Text>
                            </div>
                        </div>
                        
                        {/* Category Badge */}
                        {selectedCategory && (
                            <div style={{ marginTop: 16 }}>
                                <Tag
                                    color="white"
                                    style={{
                                        color: '#52c41a',
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        border: 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        background: 'rgba(255, 255, 255, 0.95)'
                                    }}
                                >
                                    {selectedCategory.icon} {selectedCategory.name}
                                </Tag>
                            </div>
                        )}
                    </div>
                </div>

                {/* Active Filters Tags */}
                {(filters.status !== 'all' || filters.category !== 'all') && (
                    <div style={{ 
                        marginBottom: 24,
                        padding: '16px 20px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 8 
                        }}>
                            <Text strong style={{ 
                                fontSize: '14px', 
                                color: '#475569',
                                marginRight: 8
                            }}>
                                Active Filters:
                            </Text>
                            
                            {/* Category Filter Tag */}
                            {filters.category !== 'all' && (
                                <Tag
                                    closable
                                    onClose={() => removeFilter('category')}
                                    closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                                    style={{
                                        fontSize: '13px',
                                        padding: '4px 12px',
                                        borderRadius: '16px',
                                        background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                        color: 'white',
                                        border: 'none',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    Category: {(() => {
                                        const category = categories.find(cat => 
                                            cat.name.toLowerCase() === filters.category.toLowerCase() ||
                                            cat.slug?.toLowerCase() === filters.category.toLowerCase()
                                        );
                                        return category ? category.name : filters.category;
                                    })()}
                                </Tag>
                            )}
                            
                            {/* Status Filter Tag */}
                            {filters.status !== 'all' && (
                                <Tag
                                    closable
                                    onClose={() => removeFilter('status')}
                                    closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                                    style={{
                                        fontSize: '13px',
                                        padding: '4px 12px',
                                        borderRadius: '16px',
                                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                        color: 'white',
                                        border: 'none',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    Status: {filters.status}
                                </Tag>
                            )}
                            
                            {/* Clear All Button */}
                            {(filters.status !== 'all' || filters.category !== 'all') && (
                                <Button
                                    type="text"
                                    size="small"
                                    onClick={() => {
                                        const newFilters = { status: 'all', category: 'all' };
                                        setFilters(newFilters);
                                        setLocalFilters(newFilters);
                                        navigate({
                                            pathname: '/category-results',
                                            search: ''
                                        }, { replace: true });
                                        loadCategoryResults(newFilters);
                                    }}
                                    style={{
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        padding: '0 8px',
                                        height: 'auto'
                                    }}
                                >
                                    Clear All
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {loading || categoriesLoading ? (
                    <div className="auctions-grid">
                        <Row gutter={[32, 32]}>
                            {[1, 2, 3, 4, 5, 6].map((item) => (
                                <Col xs={24} sm={12} md={8} key={item}>
                                    <AuctionCardSkeleton />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : auctions.length === 0 && !categoriesLoading ? (
                    <div className="empty-container" style={{ width: '100%' }}>
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            borderRadius: '20px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e8eaed',
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            maxWidth: 'none'
                        }}>
                            {/* Background Pattern */}
                            <div style={{
                                position: 'absolute',
                                top: -100,
                                right: -100,
                                width: 300,
                                height: 300,
                                background: 'rgba(82, 196, 26, 0.05)',
                                borderRadius: '50%',
                                zIndex: 1
                            }} />
                            <div style={{
                                position: 'absolute',
                                bottom: -80,
                                left: -80,
                                width: 200,
                                height: 200,
                                background: 'rgba(82, 196, 26, 0.03)',
                                borderRadius: '50%',
                                zIndex: 1
                            }} />
                            
                            <div style={{ position: 'relative', zIndex: 2 }}>
                                <div style={{
                                    fontSize: '64px',
                                    marginBottom: '24px',
                                    opacity: 0.6
                                }}>
                                    🔍
                                </div>
                                <Title level={3} style={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#374151',
                                    marginBottom: '12px',
                                    width: '100%'
                                }}>
                                    No auctions found
                                </Title>
                                <Text style={{
                                    fontSize: '16px',
                                    color: '#6b7280',
                                    lineHeight: 1.6,
                                    margin: '0 auto',
                                    width: '100%'
                                }}>
                                    Try adjusting your filters or browse other categories to discover amazing auctions
                                </Text>
                                <div style={{ marginTop: '24px' }}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={goBackToAuctions}
                                        style={{
                                            borderRadius: '12px',
                                            fontWeight: 600,
                                            height: '48px',
                                            padding: '0 32px',
                                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(82, 196, 26, 0.3)'
                                        }}
                                    >
                                        Browse All Auctions
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="auctions-grid">
                        <Row gutter={[32, 32]}>
                            {auctions.map((auction, index) => (
                                <Col xs={24} sm={12} md={8} key={auction.id}>
                                    <div 
                                        style={{ 
                                            position: 'relative',
                                            animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                                        }}
                                    >
                                        <AuctionCard
                                            auction={auction}
                                            onFavoriteToggle={handleFavoriteToggle}
                                        />
                                        {/* Real-time countdown overlay */}
                                        <AuctionCountdown
                                            auction={auction}
                                            variant="overlay"
                                        />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoryResults;
